import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Menu,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import { StepUpSpinner } from "../../../Components/Loader";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import SearchableSelect from "../../../Components/SearchableSelect";
import Pagination from "../../../Components/Pagination";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNotifier } from "../../../Core/Notifier";
import {
  useGetCourseQuery,
  useDeleteCourseMutation,
  useGetallGradeQuery,
  useGetallSubjectQuery,
  useGetTeamQuery,
} from "../../../Api/attoDeskApi";
import { subjectInitials, subjectTint } from "../shared/subjectColor";
import { CourseExportRow, exportCsv, exportXlsx } from "../shared/exportCourses";
import CourseEmptyState from "../shared/CourseEmptyState";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx, primaryBtnSx, secondaryBtnSx } from "../shared/moduleSx";

type CourseRow = {
  id: number;
  courseName: string;
  courseCode?: string | null;
  description?: string | null;
  status?: string | null;
  thumbnail?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  gradeId?: number | null;
  subjectId?: number | null;
  staffId?: number | null;
  materials_count?: number;
  assignments_count?: number;
  grade?: { id: number; grade: string } | null;
  subject?: { id: number; subject_name: string } | null;
  staff?: { id: number; teamFirstName?: string; teamLastName?: string } | null;
};

// Two states, two treatments. Draft = neutral (nothing published yet).
// Active = B13 primary tint. Anything else falls back to neutral.
const statusChipStyle = (s?: string | null): { bg: string; fg: string } => {
  const v = (s || "").toLowerCase();
  if (v === "active") return { bg: tint12(B13.primary), fg: B13.primary };
  return { bg: tint12(B13Ink.ink3), fg: B13Ink.ink3 };
};

const staffFullName = (s: CourseRow["staff"]): string => {
  if (!s) return "";
  return `${s.teamFirstName ?? ""} ${s.teamLastName ?? ""}`.trim();
};

// Shared filter-field sx — makes the search input and every filter
// Select feel like the same control family. Taller (44 px) than the
// default `size="small"` (~40 px), 10 px rounded corners, subtle
// hover, brand-blue focus ring so keyboard/mouse users get an
// obvious active state.
const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 44,
    borderRadius: "10px",
    bgcolor: "#FFFFFF",
    transition: "box-shadow 160ms ease, border-color 160ms ease",
    "& fieldset": { borderColor: B13.line },
    "&:hover fieldset": { borderColor: tint12(B13.primary) },
    "&.Mui-focused fieldset": {
      borderColor: B13.primary,
      borderWidth: "1px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(31,111,235,0.15)",
    },
  },
  "& .MuiSelect-select": {
    fontSize: 13.5,
    fontWeight: 500,
    color: B13.ink,
  },
} as const;

// ── View-mode primitives ─────────────────────────────────────────
// The Courses List now defaults to "Current" — Active + Draft — so
// archived rows never appear in the normal working list. Archived
// rows remain fully searchable through the explicit `Archived`
// toggle in the header. `All` is retained for admins who need the
// full history in one place.
type ViewMode = "current" | "archived" | "all";

/**
 * Status membership per view mode. Kept in one place so the toggle
 * button, the client-side row filter, and the empty-state hint all
 * agree about which statuses "belong" to each mode. Statuses that
 * fall outside these buckets (future values, misspellings) surface
 * only under `all` — safest default for an unknown state.
 */
const CURRENT_STATUSES  = new Set(["active", "draft"]);
const ARCHIVED_STATUSES = new Set(["archived", "inactive"]);
const isCurrent  = (s?: string | null) => CURRENT_STATUSES.has((s || "").toLowerCase());
const isArchived = (s?: string | null) => ARCHIVED_STATUSES.has((s || "").toLowerCase());

const CoursesList: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [searchQuery, setSearchQuery] = useState("");
  const [gradeFilter, setGradeFilter] = useState<string>("");
  const [subjectFilter, setSubjectFilter] = useState<string>("");
  const [staffFilter, setStaffFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  // Primary "which slice of history am I looking at" control. Persist
  // it in URL query so a refresh keeps the same view.
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return "current";
    const params = new URLSearchParams(window.location.search);
    const v = params.get("view");
    return v === "archived" || v === "all" ? v : "current";
  });
  // Reflect viewMode in the URL so Back/Forward preserves it and
  // deep-links like /course/list?view=archived open the archived
  // list directly.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (viewMode === "current") params.delete("view");
    else params.set("view", viewMode);
    const query = params.toString();
    const next = `${window.location.pathname}${query ? `?${query}` : ""}`;
    if (next !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState(null, "", next);
    }
  }, [viewMode]);
  const debouncedSearch = useDebounce(searchQuery, 300);

  const searchForApi = debouncedSearch.trim();

  // Deliberately omit `status` from the RTK Query request. The
  // backend endpoint only accepts a scalar status equality and has
  // no server-side pagination, so fetching once and filtering
  // client-side gives us BOTH the archived-count (for the toggle
  // badge) and a consistent view-mode + refinement pipeline without
  // any extra requests. Search / grade / subject / staff are still
  // sent to the API — those remain server-side query parameters.
  const { data, isLoading, refetch } = useGetCourseQuery({
    searchText: searchForApi,
    gradeId: gradeFilter || undefined,
    subjectId: subjectFilter || undefined,
    staffId: staffFilter || undefined,
  });
  const [deleteCourse] = useDeleteCourseMutation();

  const { data: gradeData } = useGetallGradeQuery({ searchText: "" });
  const { data: subjectData } = useGetallSubjectQuery({ searchText: "" });
  const { data: staffData } = useGetTeamQuery({ searchText: "", id: "" });

  const gradeList = useMemo(
    () => (Array.isArray(gradeData?.data) ? (gradeData!.data as any[]) : []),
    [gradeData],
  );
  const subjectList = useMemo(
    () => (Array.isArray(subjectData?.data) ? (subjectData!.data as any[]) : []),
    [subjectData],
  );
  const staffList = useMemo(
    () => (Array.isArray(staffData?.data) ? (staffData!.data as any[]) : []),
    [staffData],
  );

  // ── Client-side status + view-mode filtering ──────────────────
  // `allCourses` is the raw dataset the backend returned (already
  // narrowed by search/grade/subject/staff). We derive:
  //   - `visibleCourses` — what the grid renders, after applying
  //                       the explicit status dropdown + the view
  //                       mode.
  //   - `archivedCount`  — a live count of rows that would land in
  //                       the Archived view, used to badge the
  //                       toggle button ("Archived · 3").
  //   - `viewCounts`     — how many rows each view would show given
  //                       the current search + refinement filters,
  //                       used to differentiate a truly-empty
  //                       dataset (show onboarding) from a mode-
  //                       specific empty (show mode-aware message).
  const allCourses: CourseRow[] = useMemo(
    () => (Array.isArray(data?.data) ? (data!.data as CourseRow[]) : []),
    [data],
  );

  // Explicit status refinement (dropdown pick) takes precedence over
  // the view mode — the user asked for a specific status, respect it.
  // If no explicit status is picked, the view mode owns the filter.
  const matchesStatusRefinement = (c: CourseRow): boolean => {
    if (!statusFilter) return true;
    return (c.status ?? "").toLowerCase() === statusFilter.toLowerCase();
  };
  const matchesViewMode = (c: CourseRow): boolean => {
    if (statusFilter) return true; // explicit pick wins
    if (viewMode === "current")  return isCurrent(c.status);
    if (viewMode === "archived") return isArchived(c.status);
    return true; // "all"
  };

  const courses: CourseRow[] = useMemo(
    () => allCourses.filter((c) => matchesStatusRefinement(c) && matchesViewMode(c)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allCourses, statusFilter, viewMode],
  );

  const archivedCount = useMemo(
    () => allCourses.filter((c) => isArchived(c.status)).length,
    [allCourses],
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(12);
  useEffect(() => {
    setPage(0);
  }, [searchForApi, gradeFilter, subjectFilter, staffFilter, statusFilter, viewMode]);
  const paged = useMemo(
    () => courses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [courses, page, rowsPerPage],
  );

  const [pendingDelete, setPendingDelete] = useState<CourseRow | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const handleDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      const res = await deleteCourse(pendingDelete.id.toString()).unwrap();
      if (res.status) {
        showMessage(res.message);
        refetch();
      } else {
        showErrorMessage(res.message);
      }
    } catch {
      showErrorMessage("Failed to delete course.");
    } finally {
      setDeleteOpen(false);
      setPendingDelete(null);
    }
  }, [pendingDelete, deleteCourse, showMessage, showErrorMessage, refetch]);

  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const exportRows: CourseExportRow[] = useMemo(
    () =>
      courses.map((c) => ({
        "Course Name": c.courseName ?? "",
        Code: c.courseCode ?? "",
        Grade: c.grade?.grade ?? "",
        Subject: c.subject?.subject_name ?? "",
        Staff: staffFullName(c.staff),
        Status: (c.status ?? "").toString(),
        Materials: c.materials_count ?? "",
        Assignments: c.assignments_count ?? "",
        "Start Date": c.startDate ?? "",
        "End Date": c.endDate ?? "",
      })),
    [courses],
  );

  const clearFilters = () => {
    setSearchQuery("");
    setGradeFilter("");
    setSubjectFilter("");
    setStaffFilter("");
    setStatusFilter("");
    // NB: viewMode is intentionally NOT reset — it's a persistent
    // navigational state, not a temporary refinement. Users can
    // toggle it explicitly.
  };

  // Any active refinement filter within the current view mode.
  // The view mode itself is NOT counted so the "Clear filters"
  // affordance doesn't disappear/reappear when switching modes.
  const hasFilters =
    !!searchForApi || !!gradeFilter || !!subjectFilter || !!staffFilter || !!statusFilter;

  const exportDisabled = courses.length === 0;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      {/* ── HERO HEADER ────────────────────────────────────────────
             Reuses the shared surface treatment but adds a branded
             icon tile on the left so this page reads as the Courses
             module at a glance. Export + New Course keep their
             existing behaviour; only their styling is polished
             (taller pill height, subtle blue shadow on New Course). */}
      <Box
        sx={{
          ...surfaceSx,
          p: { xs: 2, md: 2.5 },
          mb: 2,
          borderRadius: 3,
          boxShadow: "0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(31,111,235,0.04)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: tint12(B13.primary),
                color: B13.primary,
                "& svg": { fontSize: 24 },
                flexShrink: 0,
              }}
            >
              <MenuBookOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...B13TypeScale.h2, letterSpacing: "-0.01em", lineHeight: 1.15 }}>
                Courses
              </Typography>
              <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, mt: 0.25, maxWidth: 620 }}>
                What the school offers, described for families. Assignments belong to a course.
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap" useFlexGap>
            {/* ── View-mode toggle ──────────────────────────────────
                 Three mutually-exclusive buttons in a segmented row.
                 Default is Current (Draft + Active). Archived shows
                 the historical rows; All shows everything for
                 admin/audit workflows. The badge on Archived reflects
                 the count of rows in the current search / grade /
                 subject / staff scope so users can tell at a glance
                 whether anything is hidden. */}
            {(() => {
              type Mode = { key: ViewMode; label: string; badge?: number; icon: React.ReactNode };
              const modes: Mode[] = [
                { key: "current",  label: "Current",  icon: <MenuBookOutlinedIcon sx={{ fontSize: "16px !important" }} /> },
                { key: "archived", label: "Archived", badge: archivedCount, icon: <Inventory2OutlinedIcon sx={{ fontSize: "16px !important" }} /> },
                { key: "all",      label: "All",      icon: <LayersOutlinedIcon sx={{ fontSize: "16px !important" }} /> },
              ];
              return (
                <Box
                  role="tablist"
                  aria-label="Course view"
                  sx={{
                    display: "inline-flex",
                    p: 0.5,
                    borderRadius: "12px",
                    bgcolor: "#F1F5F9",
                    border: `1px solid ${B13.line}`,
                    height: 44,
                    gap: 0.5,
                  }}
                >
                  {modes.map((m) => {
                    const active = viewMode === m.key;
                    return (
                      <Button
                        key={m.key}
                        role="tab"
                        aria-selected={active}
                        onClick={() => setViewMode(m.key)}
                        startIcon={m.icon}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: 13,
                          minHeight: 36,
                          minWidth: 0,
                          px: 1.5,
                          borderRadius: "8px",
                          color: active ? "#FFFFFF" : B13Ink.ink2,
                          bgcolor: active ? B13.primary : "transparent",
                          boxShadow: active ? "0 3px 8px rgba(31,111,235,0.24)" : "none",
                          transition: "background-color 160ms ease, color 160ms ease",
                          "& .MuiButton-startIcon": { mr: 0.5, ml: 0 },
                          "&:hover": {
                            bgcolor: active ? B13.primary : tint12(B13.primary),
                            color: active ? "#FFFFFF" : B13.primary,
                          },
                        }}
                      >
                        {m.label}
                        {typeof m.badge === "number" && m.badge > 0 && (
                          <Box
                            component="span"
                            sx={{
                              ml: 0.75,
                              px: 0.75,
                              minWidth: 20,
                              height: 18,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "9px",
                              fontSize: 11,
                              fontWeight: 800,
                              lineHeight: 1,
                              bgcolor: active ? "rgba(255,255,255,0.22)" : "#FFFFFF",
                              color: active ? "#FFFFFF" : B13Ink.ink2,
                              border: active ? "none" : `1px solid ${B13.line}`,
                            }}
                          >
                            {m.badge}
                          </Box>
                        )}
                      </Button>
                    );
                  })}
                </Box>
              );
            })()}
            <Tooltip
              title={exportDisabled ? "At least one course is required to export." : ""}
              disableHoverListener={!exportDisabled}
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadOutlinedIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={(e) => setExportAnchor(e.currentTarget)}
                  disabled={exportDisabled}
                  sx={{
                    ...secondaryBtnSx,
                    minHeight: 44,
                    borderRadius: "10px",
                    px: 2,
                  }}
                >
                  Export
                </Button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={exportAnchor}
              open={!!exportAnchor}
              onClose={() => setExportAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  exportCsv(exportRows);
                  setExportAnchor(null);
                }}
              >
                CSV
              </MenuItem>
              <MenuItem
                onClick={() => {
                  exportXlsx(exportRows);
                  setExportAnchor(null);
                }}
              >
                Excel (.xlsx)
              </MenuItem>
            </Menu>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/course/create")}
              sx={{
                ...primaryBtnSx,
                minHeight: 44,
                borderRadius: "10px",
                px: 2.25,
                boxShadow: "0 4px 10px rgba(31,111,235,0.24)",
                "&:hover": {
                  bgcolor: B13.primary,
                  filter: "brightness(0.95)",
                  boxShadow: "0 6px 14px rgba(31,111,235,0.30)",
                },
              }}
            >
              New Course
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* ── FILTER TOOLBAR ─────────────────────────────────────────
             Unified control family (see `filterFieldSx` above) — 44 px
             tall, 10 px rounded, brand-blue focus ring. Search keeps
             the widest column on desktop and gains a clear (×) icon
             that appears only when the user has typed something. The
             "Clear filters" affordance is now a compact chip-style
             text button on the right so it never crowds the row. */}
      <Box
        sx={{
          ...surfaceSx,
          p: { xs: 1.75, md: 2 },
          mb: 2,
          borderRadius: 3,
          boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search courses by name, code, description"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={filterFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CiSearch size={18} color={B13Ink.ink3} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setSearchQuery("")}
                      sx={{
                        color: B13Ink.ink3,
                        "&:hover": { color: B13.primary, bgcolor: tint12(B13.primary) },
                      }}
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" sx={filterFieldSx}>
              <Select
                displayEmpty
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value as string)}
              >
                <MenuItem value="">All grades</MenuItem>
                {gradeList.map((g: any) => (
                  <MenuItem key={g.id} value={String(g.id)}>
                    {g.grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            {/* Subject — searchable (list can be dozens of subjects
                including compound names like "Math - Advanced
                Functions"). Options are mapped from the same
                `useGetallSubjectQuery` payload as before; only the
                dropdown shell is different. */}
            <SearchableSelect
              sx={filterFieldSx}
              value={subjectFilter}
              onChange={setSubjectFilter}
              allLabel="All subjects"
              searchPlaceholder="Search subjects…"
              noResultsText="No subjects found"
              ariaLabel="Subject filter"
              options={subjectList.map((s: any) => ({
                value: String(s.id),
                label: String(s.subject_name ?? ""),
              }))}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            {/* Staff — searchable (long list, often 60+ names). Label
                mirrors what the plain Select rendered before so
                selecting doesn't visually change existing rows. */}
            <SearchableSelect
              sx={filterFieldSx}
              value={staffFilter}
              onChange={setStaffFilter}
              allLabel="All staff"
              searchPlaceholder="Search staff…"
              noResultsText="No staff found"
              ariaLabel="Staff filter"
              options={staffList.map((s: any) => {
                const name = `${s.teamFirstName ?? ""} ${s.teamLastName ?? ""}`.trim();
                return {
                  value: String(s.id),
                  label: name || `Staff #${s.id}`,
                };
              })}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" sx={filterFieldSx}>
              <Select
                displayEmpty
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as string)}
                aria-label="Status filter"
              >
                {/* The empty option's LABEL follows the active view
                    mode so the resting state of the control tells the
                    user what they're actually looking at — "Current
                    courses" by default, not the misleading "All
                    statuses". Selecting a specific status still
                    refines within the current results.  */}
                <MenuItem value="">
                  {viewMode === "current"
                    ? "Current courses"
                    : viewMode === "archived"
                    ? "All archived"
                    : "All statuses"}
                </MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {hasFilters && (
          <Box sx={{ mt: 1.25, display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              startIcon={<CloseRoundedIcon sx={{ fontSize: "16px !important" }} />}
              onClick={clearFilters}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                color: B13.primary,
                borderRadius: "8px",
                px: 1.25,
                "&:hover": { bgcolor: tint12(B13.primary) },
              }}
            >
              Clear filters
            </Button>
          </Box>
        )}
      </Box>

      {/* Body */}
      {isLoading ? (
        <Box sx={{ ...surfaceSx, py: 6, display: "flex", justifyContent: "center" }}>
          <StepUpSpinner size="large" label="Loading courses" />
        </Box>
      ) : courses.length === 0 && !hasFilters && allCourses.length === 0 ? (
        // True empty catalog — no courses exist under any view.
        // Fires the onboarding hero regardless of the current mode.
        <CourseEmptyState onCreate={() => navigate("/course/create")} />
      ) : courses.length === 0 ? (
        // Mode-specific / filter-specific empty. The copy adapts so
        // the user understands whether their search just excluded
        // everything or whether the view they're on genuinely has
        // nothing to show.
        (() => {
          const noCurrentButHasArchive =
            viewMode === "current" && !hasFilters && archivedCount > 0;
          const title =
            noCurrentButHasArchive
              ? "No current courses"
              : viewMode === "archived" && !hasFilters
              ? "No archived courses"
              : "No courses match the current filters.";
          const hint =
            noCurrentButHasArchive
              ? `${archivedCount} archived course${archivedCount === 1 ? "" : "s"} available in the Archived view.`
              : viewMode === "archived" && !hasFilters
              ? "Courses archived from the Current view will appear here."
              : "Try clearing a filter or refining your search.";
          return (
            <Box
              sx={{
                ...surfaceSx,
                py: { xs: 5, md: 6 },
                textAlign: "center",
                borderRadius: 3,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2.5,
                  mx: "auto",
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: tint12(B13.primary),
                  color: B13.primary,
                  "& svg": { fontSize: 26 },
                }}
              >
                {viewMode === "archived" ? <Inventory2OutlinedIcon /> : <MenuBookOutlinedIcon />}
              </Box>
              <Typography sx={{ ...B13TypeScale.bodyStrong }}>{title}</Typography>
              <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, mt: 0.5 }}>
                {hint}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                {noCurrentButHasArchive && (
                  <Button
                    variant="outlined"
                    startIcon={<Inventory2OutlinedIcon />}
                    sx={{ ...secondaryBtnSx, minHeight: 44, borderRadius: "10px", px: 2 }}
                    onClick={() => setViewMode("archived")}
                  >
                    View archived
                  </Button>
                )}
                {hasFilters && (
                  <Button
                    variant="outlined"
                    sx={{ ...secondaryBtnSx, minHeight: 44, borderRadius: "10px", px: 2 }}
                    onClick={clearFilters}
                  >
                    Clear filters
                  </Button>
                )}
              </Stack>
            </Box>
          );
        })()
      ) : (
        <>
          <Grid container spacing={2}>
            {paged.map((c) => {
              const tint = subjectTint(c.subject?.subject_name || c.courseName);
              const initials = subjectInitials(c.subject?.subject_name || c.courseName);
              const cover = c.thumbnail || null;
              const staffName = staffFullName(c.staff) || "Unassigned";
              const statusStyle = statusChipStyle(c.status);
              return (
                <Grid key={c.id} item xs={12} sm={6} md={4} lg={3}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      cursor: "pointer",
                      bgcolor: B13.surface,
                      border: `1px solid ${B13.line}`,
                      transition: "border-color 0.15s ease, background-color 0.15s ease",
                      "&:hover": {
                        borderColor: B13.primary,
                        bgcolor: tint12(B13.primary),
                      },
                    }}
                    onClick={() => navigate(`/course/${c.id}`)}
                  >
                    {/* Cover: thumbnail if present, otherwise subject tint block
                        with initials — subjectColor.ts keeps its role as the
                        one place colour differentiates courses. */}
                    <Box
                      sx={{
                        height: 96,
                        bgcolor: tint.bg,
                        color: tint.fg,
                        borderBottom: `1px solid ${B13.line}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      {cover ? (
                        // eslint-disable-next-line jsx-a11y/alt-text
                        <img
                          src={cover}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 30, fontWeight: 800, letterSpacing: 1 }}>
                          {initials}
                        </Typography>
                      )}
                      <Chip
                        label={(c.status ?? "").toString() || "—"}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          textTransform: "capitalize",
                          fontWeight: 600,
                          bgcolor: statusStyle.bg,
                          color: statusStyle.fg,
                          border: "none",
                        }}
                      />
                    </Box>

                    <Box sx={{ p: 1.75, flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                      <Box>
                        <Typography
                          sx={{ ...B13TypeScale.bodyStrong, lineHeight: 1.2 }}
                          noWrap
                        >
                          {c.courseName}
                        </Typography>
                        {c.courseCode ? (
                          <Typography sx={{ ...B13TypeScale.caption, mt: 0.5 }}>
                            {c.courseCode}
                          </Typography>
                        ) : null}
                      </Box>

                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {c.grade?.grade ? (
                          <Chip
                            label={`Grade ${c.grade.grade}`}
                            size="small"
                            sx={{
                              height: 22,
                              bgcolor: "transparent",
                              border: `1px solid ${B13.line}`,
                              color: B13Ink.ink2,
                              fontSize: 11,
                            }}
                          />
                        ) : null}
                        {c.subject?.subject_name ? (
                          <Chip
                            label={c.subject.subject_name}
                            size="small"
                            sx={{
                              height: 22,
                              bgcolor: "transparent",
                              border: `1px solid ${B13.line}`,
                              color: B13Ink.ink2,
                              fontSize: 11,
                            }}
                          />
                        ) : null}
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: "auto" }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: 12,
                            bgcolor: tint12(B13.primary),
                            color: B13.primary,
                          }}
                        >
                          {(staffName[0] || "?").toUpperCase()}
                        </Avatar>
                        <Typography sx={{ ...B13TypeScale.caption }} noWrap>
                          {staffName}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={2} sx={{ pt: 0.5 }}>
                        <Tooltip title="Course materials">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AttachFileIcon sx={{ fontSize: 14, color: B13Ink.ink3 }} />
                            <Typography sx={{ ...B13TypeScale.num, fontSize: 12 }}>
                              {c.materials_count ?? 0}
                            </Typography>
                          </Stack>
                        </Tooltip>
                        <Tooltip title="Assignments">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AssignmentOutlinedIcon sx={{ fontSize: 14, color: B13Ink.ink3 }} />
                            <Typography sx={{ ...B13TypeScale.num, fontSize: 12 }}>
                              {c.assignments_count ?? 0}
                            </Typography>
                          </Stack>
                        </Tooltip>
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderTop: `1px solid ${B13.line}`,
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 0.5,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => navigate(`/course/${c.id}`)}
                          sx={{ color: B13Ink.ink3, "&:hover": { color: B13.primary } }}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => navigate(`/course/edit/${c.id}`)}
                          sx={{ color: B13Ink.ink3, "&:hover": { color: B13.primary } }}>
                          <EditSharpIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPendingDelete(c);
                            setDeleteOpen(true);
                          }}
                          sx={{ color: B13Ink.ink3, "&:hover": { color: "#E11D48" } }}
                        >
                          <DeleteOutlineSharpIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {courses.length > rowsPerPage && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Pagination
                count={courses.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_e, p) => setPage(p)}
              />
            </Box>
          )}
        </>
      )}

      {pendingDelete && (
        <DeletePopup
          open={deleteOpen}
          handleCloseDelete={() => {
            setDeleteOpen(false);
            setPendingDelete(null);
          }}
          onConfirm={handleDelete}
          title="Delete Course"
          content={`Are you sure you want to delete "${pendingDelete.courseName}"?`}
        />
      )}
    </Box>
  );
};

export default CoursesList;
